import type { Database, MissionDetails } from '../../types';

type MissionRow = Database['public']['Tables']['missions']['Row'];

function toTitleCase(str: string): string {
  if (!str) return str;
  return str.toLowerCase().replace(/(?:^|[\s\-/])\S/g, (c) => c.toUpperCase());
}

export function mapDbMissionToMissionDetails(row: MissionRow): MissionDetails | null {
  let rawData = row.summary_json as unknown;

  if (typeof rawData === 'string') {
    try {
      rawData = JSON.parse(rawData);
    } catch (e) {
      console.error('Failed to parse mission summary_json', e);
    }
  }

  let data: any = {};
  if (Array.isArray(rawData) && rawData.length > 0) {
    data = rawData[0];
  } else if (rawData && typeof rawData === 'object') {
    data = rawData;
  }

  const hasRaw = data.raw && Object.keys(data.raw).length > 0;
  const hasProfile = data.profile && Object.keys(data.profile).length > 0;

  const currentStatus = ((row as any).status || data.status || '').toLowerCase().trim();
  const isProcessingOrSpecial = [
    'reçu',
    'recu',
    'analyse type de document',
    'analyse_type',
    'analyse des agents',
    'analyse_agents',
    'analyse complete',
    'analyse_complete',
    'analyse complète',
    'mission existante',
    'mission_existante',
    'erreur',
  ].includes(currentStatus);

  if (!isProcessingOrSpecial && !row.mission_title && !row.client_name && !hasRaw && !hasProfile && !data.title) {
    return null;
  }

  const getVal = (val: any, fallback = 'Non communiqué') => {
    if (val === null || val === undefined || val === '' || val === 'nc') return fallback;
    return val;
  };

  const raw = data.raw || {};
  const profile = data.profile || {};
  const insights = data.insights || {};
  const ads = data.ads || {};

  // Legacy backward compat
  const textObj = data.text || {};
  const header = textObj.header || data.header || {};
  const overview = textObj.overview || data.overview || {};
  const details = textObj.details || data.details || {};
  const callKit = textObj.call_kit || data.call_kit || {};
  const competences = textObj.competences || data.competences || {};

  const mergeRisks = (...arrays: any[]) => {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const arr of arrays) {
      if (!Array.isArray(arr)) continue;
      for (const r of arr) {
        const level = r?.severity || r?.impact || '';
        const str = typeof r === 'string'
          ? r
          : r?.label
            ? (level ? `${r.label} : ${level}` : r.label)
            : JSON.stringify(r);
        if (!seen.has(str)) { seen.add(str); result.push(str); }
      }
    }
    return result;
  };
  const risks = mergeRisks(
    insights.risques_missions,
    insights.risques_administratifs,
    details.risques,
    data.risks,
  );

  let pitchCandidate = '';
  if (ads.job_board_ad?.intro) {
    pitchCandidate = ads.job_board_ad.intro;
  } else if (Array.isArray(callKit.arguments_candidat)) {
    pitchCandidate = callKit.arguments_candidat.map((arg: string) => `• ${arg}`).join('\n');
  } else {
    pitchCandidate = data.pitch?.candidate || '';
  }

  const mapSkills = (skillsArray: any[], fallbackImportance: 'MUST_HAVE' | 'NICE_TO_HAVE') => {
    if (!Array.isArray(skillsArray)) return [];
    return skillsArray.map(s => {
      if (typeof s === 'string') return { name: s, importance: fallbackImportance };
      return { name: s?.name || s?.label || 'Unknown', importance: s?.importance || fallbackImportance };
    });
  };

  const technicalSkills = mapSkills(
    profile.competency_matrix?.technical_skills ||
      competences.stack_technique ||
      raw.main_technical_stack ||
      data.requiredSkills?.technical ||
      [],
    'MUST_HAVE'
  );
  const methodsSkills = mapSkills(
    profile.competency_matrix?.methods_tools_skills ||
      competences.expertise ||
      raw.methodologies ||
      data.requiredSkills?.methods ||
      [],
    'NICE_TO_HAVE'
  );
  const softSkills = mapSkills(
    profile.competency_matrix?.soft_skills || raw.soft_skills || data.requiredSkills?.soft || [],
    'MUST_HAVE'
  );

  const statusDb = (row as any).status || data.status || 'Prospection';

  // Dedicated columns (always set from trigger), fallback to summary_json raw fields
  const colTjmMin = (row as any).tjm_min as number | null;
  const colTjmMax = (row as any).tjm_max as number | null;
  const tjmHasValue = colTjmMin != null && colTjmMax != null && (colTjmMin > 0 || colTjmMax > 0);
  // Un TJM absent de l'annonce est extrait à 0 : c'est une absence, pas « 0 - 0 € ».
  const rawMin = Number(raw.tjm_min);
  const rawMax = Number(raw.tjm_max);
  const rawHasValue =
    (Number.isFinite(rawMin) && rawMin > 0) || (Number.isFinite(rawMax) && rawMax > 0);
  const tjmRaw = tjmHasValue
    ? `${colTjmMin} - ${colTjmMax} €`
    : rawHasValue
      ? `${raw.tjm_min} - ${raw.tjm_max} ${raw.tjm_currency || '€'}`
      : 'Non communiqué';

  return {
    id: row.id.toString(),
    client: toTitleCase(
      row.client_name ||
      getVal(raw.end_client_name) ||
      header.client_name ||
      data.client ||
      'Unknown Client'
    ),
    title: toTitleCase(
      row.mission_title ||
      getVal(raw.mission_title) ||
      header.mission_title ||
      data.title ||
      'Untitled Mission'
    ),
    complexity: data.complexity || 'Moyenne',
    duration:
      getVal(raw.duration_months) !== 'Non communiqué'
        ? `${raw.duration_months} mois`
        : overview.duree || 'Non communiqué',
    tjm: tjmRaw !== 'Non communiqué' ? tjmRaw : overview.tjm || 'Non communiqué',
    location:
      (row as any).location_city ||
      (getVal(raw.location_city) !== 'Non communiqué' ? raw.location_city : null) ||
      overview.localisation ||
      'Non communiqué',
    status: statusDb as any,
    datePosted:
      getVal(raw.start_date_raw) !== 'Non communiqué'
        ? raw.start_date_raw
        : (row as any).created_at || new Date().toISOString(),
    applicants: data.applicants || 0,
    missionSummary:
      getVal(raw.mission_summary) !== 'Non communiqué'
        ? raw.mission_summary
        : overview.resume_besoin || 'Non communiqué',
    dealBreakers:
      profile.sourcing_strategy?.deal_breakers || competences.deal_breakers || data.dealBreakers || [],
    strategicContext:
      insights.strategic_notes && insights.strategic_notes.length > 0
        ? insights.strategic_notes.join('\n')
        : data.strategicContext || 'Non communiqué',
    requiredSkills: { technical: technicalSkills, methods: methodsSkills, soft: softSkills },
    pitch: { sales: getVal(data.pitch?.sales, ''), candidate: pitchCandidate },
    kitDetails: {
      contractualQuestions:
        insights.questions_a_poser?.contractual ||
        insights.questions_admin_contractual ||
        data.kitDetails?.contractualQuestions ||
        [],
      technicalQuestions:
        insights.questions_a_poser?.technical ||
        insights.questions_technical_scope ||
        data.kitDetails?.technicalQuestions ||
        [],
      screeningQuestions:
        profile.sourcing_strategy?.screening_questions || data.kitDetails?.screeningQuestions || [],
    },
    standardized_title:
      insights.title_analysis?.real_market_title ||
      header.title_analysis?.real_market_title ||
      'Non communiqué',
    deal_summary_one_line:
      getVal(insights.deal_summary_one_line) !== 'Non communiqué'
        ? insights.deal_summary_one_line
        : overview.resume_besoin || 'Non communiqué',
    questions_technical_scope:
      insights.questions_a_poser?.technical ||
      insights.questions_technical_scope ||
      callKit.questions_tech ||
      [],
    questions_admin_contractual:
      insights.questions_a_poser?.contractual ||
      insights.questions_admin_contractual ||
      callKit.questions_admin ||
      [],
    risks,
    interviewKit: {
      admin:
        insights.questions_a_poser?.contractual ||
        insights.questions_admin_contractual ||
        callKit.questions_admin ||
        data.interviewKit?.admin ||
        [],
      tech:
        insights.questions_a_poser?.technical ||
        insights.questions_technical_scope ||
        callKit.questions_tech ||
        data.interviewKit?.tech ||
        [],
    },
    booleanSearch: profile.sourcing_strategy?.boolean_search || data.booleanSearch,
    persona: profile.sourcing_strategy?.persona_archetype || data.persona,
    other_explicit_info: raw.other_explicit_info || data.other_explicit_info || [],
    linkedin_dm: ads.outreach_candidate_message || data.linkedin_dm,
    linkedin_hook: ads.linkedin_hook || '',
    linkedin_post: ads.linkedin_post || '',
    job_board_ad: ads.job_board_ad
      ? {
          title: ads.job_board_ad.title || '',
          intro: ads.job_board_ad.intro || '',
          tasks: Array.isArray(ads.job_board_ad.tasks) ? ads.job_board_ad.tasks : [],
          technical_requirements: ads.job_board_ad.technical_requirements || '',
          soft_skills_mindset: ads.job_board_ad.soft_skills_mindset || '',
        }
      : undefined,
    outreach_candidate_message: ads.outreach_candidate_message || '',
    outreach_message_short: ads.outreach_message_short || '',
    outreach_message_full: ads.outreach_message_full || '',
    suggested_hashtags: Array.isArray(ads.suggested_hashtags) ? ads.suggested_hashtags : [],
    originalJobText: (row as any).original_jd_text || undefined,
    summary_json: rawData,
    assigned_to: (row as any).assigned_to ?? null,
    created_by: (row as any).created_by ?? null,
    client_id: (row as any).client_id ?? null,
    contact_id: (row as any).contact_id ?? null,
  };
}
